import { LoadingOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import useImage from '../../../hooks/useImage';
import { RoundMedia } from '../../../services/backendService/types';
import { zIndexes } from '../../../utils/zIndexes';
import { SlideLogo } from '../../_atoms/Logo';

const aspectRatio = 16 / 9;
export const SlideWrapperContext = React.createContext<{
  slideWrapperHeight: number;
  slideWrapperWidth: number;
  disabledMedia: boolean;
}>({
  slideWrapperHeight: 9,
  slideWrapperWidth: 16,
  disabledMedia: false,
});

const SlideWrapper = ({
  backgroundMedia,
  children,
  style,
  overflowVisible,
  disabledMedia = false,
  isAuthenticated,
  isDiscoverTemplate,
  isOwner,
  contentWrapperStyles,
  useThumbnail,
}: {
  backgroundMedia?: RoundMedia | null | undefined;
  children?: React.ReactNode;
  style?: CSSProperties;
  contentWrapperStyles?: CSSProperties;
  overflowVisible?: boolean;
  disabledMedia?: boolean;
  isAuthenticated: boolean;
  isDiscoverTemplate: boolean;
  isOwner: boolean;
  useThumbnail?: boolean;
}) => {
  const parentContentRef = useRef<HTMLDivElement>(null);
  const [parentContainerWidth, setParentContainerWidth] = useState<number>(16);
  const [parentContainerHeight, setParentContainerHeight] = useState<number>(9);

  const backgroundMediaFileId = useMemo(() => {
    if (backgroundMedia?.fileMimeType === 'image/unsplash') {
      if (useThumbnail && backgroundMedia.thumbnailSrc)
        return backgroundMedia.thumbnailSrc;
    }
    return backgroundMedia?.fileId;
  }, [backgroundMedia, useThumbnail]);

  const { url, isLoading } = useImage({
    fileId: backgroundMediaFileId,
    type: backgroundMedia?.fileMimeType,
    cropConfig: backgroundMedia?.cropConfig,
    isAuthenticated,
    isDiscoverTemplate,
    isOwner,
  });
  const slideRef = useRef<HTMLDivElement | null>(null);

  const observer = useRef(
    new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        // Only care about the first element, we expect one element ot be watched
        const { width: parentwidth, height: parentHeight } = entries[0].contentRect;
        setParentContainerWidth(parentwidth);
        setParentContainerHeight(parentHeight);
      });
    }),
  );

  useEffect(() => {
    const observerRef = observer.current;
    const mediaRef = parentContentRef.current;
    if (mediaRef) {
      observer.current.observe(mediaRef);
    }

    return () => {
      if (mediaRef && observerRef) {
        observerRef.unobserve(mediaRef);
      }
    };
  }, [parentContentRef, observer]);

  // TODO: make this prettier, also embed it into a function which we can have unit tests
  const isScaleLimitedByHeight =
    parentContainerWidth / parentContainerHeight > aspectRatio; // the aspect ratio calculated is bigger than the given aspect ratio
  const currentHeight = isScaleLimitedByHeight
    ? parentContainerHeight
    : parentContainerWidth / aspectRatio;
  const currentWidth = isScaleLimitedByHeight
    ? aspectRatio * parentContainerHeight
    : parentContainerWidth;

  return (
    <ContentWrapper
      ref={parentContentRef}
      $overflowVisible={overflowVisible}
      style={contentWrapperStyles ? contentWrapperStyles : {}}
    >
      <ContentContainer
        $aspectRatio={aspectRatio}
        $backgroundMedia={url}
        $isColorBackground={!!url && url.startsWith('#')}
        $brightness={backgroundMedia?.brightness}
        $currentHeight={currentHeight}
        $currentWidth={currentWidth}
        style={style ? style : {}}
        ref={slideRef}
        $isScaleLimitedByHeight={isScaleLimitedByHeight}
      >
        <SlideWrapperContext.Provider
          value={{
            slideWrapperHeight: currentHeight,
            slideWrapperWidth: currentWidth,
            disabledMedia,
          }}
        >
          {isLoading && (
            <div style={{ position: 'absolute', top: 0, left: '50%' }}>
              <LoadingOutlined />
            </div>
          )}

          <span
            style={{
              position: 'absolute',
              right: '2%',
              top: '0%',
              cursor: 'default',
              zIndex: zIndexes('CURIPOD_LOGO'),
            }}
          >
            <SlideLogo
              color={theme.colors.orange}
              cursor="default"
              width={
                slideRef.current?.offsetWidth
                  ? `${slideRef.current?.offsetWidth / 10}px`
                  : undefined
              }
              height={
                slideRef.current?.offsetHeight
                  ? `${slideRef.current?.offsetHeight / 10}px`
                  : undefined
              }
            />
          </span>
          {children}
        </SlideWrapperContext.Provider>
      </ContentContainer>
    </ContentWrapper>
  );
};
/* TODO: Rewrite this background css for easier readablility */
const ContentContainer = styled.div<{
  $aspectRatio: number;
  $backgroundMedia?: string | null;
  $maxWidth?: number;
  $currentWidth: number;
  $currentHeight: number;
  $brightness?: number | null;
  $isColorBackground: boolean;
  $isScaleLimitedByHeight: boolean;
}>`
  ${({ $isScaleLimitedByHeight }) =>
    $isScaleLimitedByHeight
      ? css`
          height: 100%;
          width: fit-content;
        `
      : css`
          height: fit-content;
          width: 100%;
        `};

  aspect-ratio: ${({ $aspectRatio }) => ($aspectRatio ? $aspectRatio : '16/9')};
  position: relative;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);

  ${({ $backgroundMedia, $brightness, $isColorBackground }) =>
    $backgroundMedia && !$isColorBackground
      ? // Background media is selected
        css`
          background: url(${$backgroundMedia});
          ${(!$brightness || $brightness === 0.5) &&
            css`
              background: url(${$backgroundMedia});
              background-size: cover;
              background-position: center;
            `};
          ${$brightness &&
            $brightness > 0.5 &&
            css`
              background: linear-gradient(
                  rgba(255, 255, 255, ${$brightness - 0.5}),
                  rgba(255, 255, 255, ${$brightness - 0.5})
                ),
                url(${$backgroundMedia});
              background-size: cover;
              background-position: center;
            `};
          ${$brightness &&
            $brightness < 0.5 &&
            css`
              background: linear-gradient(
                  rgba(0, 0, 0, ${0.5 - $brightness}),
                  rgba(0, 0, 0, ${0.5 - $brightness})
                ),
                url(${$backgroundMedia});
              background-size: cover;
              background-position: center;
            `};
        `
      : $isColorBackground && $backgroundMedia
      ? // Color background is selected
        css`
          ${(!$brightness || $brightness === 0.5) &&
            css`
              background: ${$backgroundMedia};
            `};
          ${$brightness &&
            $brightness > 0.5 &&
            css`
              background: linear-gradient(
                  rgba(255, 255, 255, ${$brightness - 0.5}),
                  rgba(255, 255, 255, ${$brightness - 0.5})
                ),
                ${$backgroundMedia};
            `};
          ${$brightness &&
            $brightness < 0.5 &&
            css`
              background: linear-gradient(
                  rgba(0, 0, 0, ${0.5 - $brightness}),
                  rgba(0, 0, 0, ${0.5 - $brightness})
                ),
                ${$backgroundMedia};
            `};
        `
      : // No background is selected
        css`
          background: ${theme.colors.background};
        `};
  --scale-factor: ${({
    $currentWidth,
    $currentHeight,
    $isScaleLimitedByHeight,
    $aspectRatio,
  }) =>
    `${
      $isScaleLimitedByHeight ? $currentHeight * $aspectRatio : $currentWidth
    }px`}; // the parent width that Slate use to relative scale the sizes */
`;

const ContentWrapper = styled.div<{ $overflowVisible?: boolean }>`
  width: 100%;
  background: white;
  box-sizing: border-box;
  overflow: hidden;
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  overflow: hidden;
  height: 100%;
  overflow: ${({ $overflowVisible }) => ($overflowVisible ? 'visible' : 'hidden')};
  background-color: transparent;
`;

export default SlideWrapper;
